import { Box } from '@mui/material';
import { SearchOutlined } from '@mui/icons-material';
import { ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { tokens } from '@synergy/theme-palo';
import { usePaloShortcut } from './palo-shortcuts';
import { paloLinkReset, paloOpenInNewTab, paloWantsNewTab } from './palo-link';

const c = tokens.color;

export interface PaloCommand {
    label: string;
    /** Secondary line (e.g. the area or route). */
    sub?: string;
    /** Small keyboard-hint chip on the right. */
    keyHint?: string;
    icon?: ReactNode;
    run: () => void;
    /**
     * Where the command leads, if anywhere. The entry becomes a link, so
     * Ctrl/⌘, the middle button and the context menu open it in a new tab;
     * Ctrl/⌘ + Enter does the same from the keyboard (#2327).
     */
    href?: string;
}

export interface PaloCommandGroup {
    label: string;
    items: PaloCommand[];
    /**
     * The group's items already answer the query — they came from a search
     * rather than from a fixed list — so the palette must not filter them
     * again. Its own matching only knows the label and the sub-line, and would
     * throw away a document that matched on something else.
     */
    preFiltered?: boolean;
}

export interface PaloCommandPaletteProps {
    open: boolean;
    onClose: () => void;
    groups: PaloCommandGroup[];
    /** Right-aligned context chip in the input row (e.g. the active area). */
    contextLabel?: string;
    placeholder?: string;
    /** Fires as the user types, so the host can search on the server. */
    onQueryChange?: (query: string) => void;
    /** Shown while the host is searching. */
    searching?: boolean;
}

const matches = (command: PaloCommand, query: string): boolean =>
    !query ||
    command.label.toLowerCase().includes(query) ||
    (command.sub?.toLowerCase().includes(query) ?? false);

const highlight = (label: string, query: string): ReactNode => {
    if (!query) {
        return label;
    }
    const pos = label.toLowerCase().indexOf(query);
    if (pos < 0) {
        return label;
    }

    return (
        <>
            {label.slice(0, pos)}
            <Box component="em" sx={{ fontStyle: 'normal', backgroundColor: 'rgba(232,133,15,.24)', borderRadius: '2px', px: '1px' }}>
                {label.slice(pos, pos + query.length)}
            </Box>
            {label.slice(pos + query.length)}
        </>
    );
};

/**
 * PALO command palette (⌘K) per the prototype: a scrim with a search input, a
 * grouped, keyboard-navigable command list and a footer. Presentational — the
 * host supplies real jump targets and actions; open/close state is external.
 */
export const PaloCommandPalette = ({
    open,
    onClose,
    groups,
    contextLabel,
    placeholder = 'Suchen, springen oder Befehl ausführen …',
    onQueryChange,
    searching,
}: PaloCommandPaletteProps): ReactElement | null => {
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState(0);

    // Registered while open so that it shadows whatever Esc means underneath —
    // the mask's "go back" must not fire through an open palette.
    usePaloShortcut(open ? { combo: 'Escape', label: 'Palette schließen', hidden: true, handler: onClose } : undefined);
    const inputRef = useRef<HTMLInputElement>(null);

    const filteredGroups = useMemo(() => {
        const q = query.trim().toLowerCase();

        return groups
            .map(group =>
                group.preFiltered ? group : { ...group, items: group.items.filter(item => matches(item, q)) }
            )
            .filter(group => group.items.length > 0);
    }, [groups, query]);

    const flat = useMemo(() => filteredGroups.flatMap(group => group.items), [filteredGroups]);

    useEffect(() => {
        if (open) {
            setQuery('');
            setSelected(0);
            const raf = requestAnimationFrame(() => inputRef.current?.focus());

            return () => cancelAnimationFrame(raf);
        }

        return undefined;
    }, [open]);

    useEffect(() => {
        setSelected(0);
    }, [query]);

    useEffect(() => {
        if (!open || !onQueryChange) {
            return undefined;
        }

        // Debounced: a palette is typed into fast, and every keystroke would
        // otherwise be a round trip per document type.
        const timer = setTimeout(() => onQueryChange(query.trim()), 200);

        return () => clearTimeout(timer);
    }, [open, query, onQueryChange]);

    if (!open) {
        return null;
    }

    const run = (command?: PaloCommand): void => {
        if (command) {
            command.run();
            onClose();
        }
    };

    const onKeyDown = (event: React.KeyboardEvent): void => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setSelected(value => (flat.length ? (value + 1) % flat.length : 0));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setSelected(value => (flat.length ? (value - 1 + flat.length) % flat.length : 0));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            const command = flat[selected];

            if (command?.href && (event.ctrlKey || event.metaKey)) {
                paloOpenInNewTab(command.href);
            } else {
                run(command);
            }
        } else if (event.key === 'Escape') {
            event.preventDefault();
            onClose();
        }
    };

    const q = query.trim().toLowerCase();
    let running = -1;

    return (
        <Box
            onMouseDown={event => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
            sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 1400,
                backgroundColor: 'rgba(19,24,30,.42)',
                backdropFilter: 'blur(1.5px)',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                paddingTop: '76px',
            }}
        >
            <Box
                onKeyDown={onKeyDown}
                sx={{
                    width: 'min(556px, 92%)',
                    backgroundColor: c.surface,
                    borderRadius: '10px',
                    boxShadow: tokens.shadow.s3,
                    overflow: 'hidden',
                    fontFamily: tokens.font.sans,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '13px 15px', borderBottom: `1px solid ${c.line}` }}>
                    <SearchOutlined sx={{ fontSize: 17, color: c.ink3, flexShrink: 0 }} />
                    <Box
                        component="input"
                        ref={inputRef}
                        value={query}
                        placeholder={placeholder}
                        onChange={event => setQuery((event.target as HTMLInputElement).value)}
                        sx={{ flex: 1, border: 0, outline: 0, fontSize: '15px', background: 'transparent', color: c.ink, fontFamily: 'inherit', '&::placeholder': { color: c.ink3 } }}
                    />
                    {contextLabel && (
                        <Box sx={{ fontSize: '10.5px', backgroundColor: c.accentSoft, color: c.accentHi, padding: '3px 8px', borderRadius: '10px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {contextLabel}
                        </Box>
                    )}
                </Box>

                <Box sx={{ maxHeight: 316, overflowY: 'auto', padding: '5px' }}>
                    {flat.length === 0 && (
                        <Box sx={{ padding: '18px 12px', fontSize: '13px', color: c.ink3, textAlign: 'center' }}>
                            Keine Treffer für „{query}"
                        </Box>
                    )}
                    {filteredGroups.map(group => (
                        <Box key={group.label}>
                            <Box sx={{ fontSize: '9.5px', fontWeight: 650, letterSpacing: '.09em', textTransform: 'uppercase', color: c.ink3, padding: '9px 11px 4px' }}>
                                {group.label}
                            </Box>
                            {group.items.map(item => {
                                running += 1;
                                const isOn = running === selected;
                                const index = running;

                                return (
                                    <Box
                                        key={item.label + (item.sub ?? '')}
                                        {...(item.href ? { component: 'a', href: item.href } : {})}
                                        onMouseMove={() => setSelected(index)}
                                        onClick={(event: React.MouseEvent) => {
                                            // The browser opens the link in a new tab itself;
                                            // the palette stays, to open the next one.
                                            if (item.href && paloWantsNewTab(event)) {
                                                return;
                                            }

                                            event.preventDefault();
                                            run(item);
                                        }}
                                        sx={{
                                            ...paloLinkReset,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '11px',
                                            padding: '8px 11px',
                                            borderRadius: tokens.radius.small + 'px',
                                            cursor: 'pointer',
                                            backgroundColor: isOn ? c.accentSoft : 'transparent',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 26,
                                                height: 26,
                                                borderRadius: tokens.radius.small + 'px',
                                                display: 'grid',
                                                placeItems: 'center',
                                                flexShrink: 0,
                                                backgroundColor: isOn ? c.accent : c.neutralSoft,
                                                color: isOn ? '#fff' : c.ink2,
                                                '& svg': { fontSize: 14 },
                                            }}
                                        >
                                            {item.icon}
                                        </Box>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Box sx={{ fontSize: '13px', fontWeight: 550, color: c.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {highlight(item.label, q)}
                                            </Box>
                                            {item.sub && <Box sx={{ fontSize: '11px', color: c.ink3, mt: '1px' }}>{item.sub}</Box>}
                                        </Box>
                                        {item.keyHint && (
                                            <Box sx={{ fontFamily: tokens.font.mono, fontSize: '10px', color: c.ink3, border: `1px solid ${c.lineHard}`, borderRadius: '3px', padding: '1px 5px', whiteSpace: 'nowrap' }}>
                                                {item.keyHint}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    ))}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px', padding: '8px 15px', borderTop: `1px solid ${c.line}`, backgroundColor: c.surfaceAlt, fontSize: '10.5px', color: c.ink3 }}>
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Kbd>↑↓</Kbd> Navigieren
                    </Box>
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Kbd>↵</Kbd> Öffnen
                    </Box>
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '5px', marginLeft: 'auto' }}>
                        <Kbd>esc</Kbd> Schließen
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

const Kbd = ({ children }: { children: ReactNode }): ReactElement => (
    <Box component="span" sx={{ fontFamily: tokens.font.mono, fontSize: '10px', color: c.ink3, border: `1px solid ${c.lineHard}`, borderRadius: '3px', padding: '1px 5px' }}>
        {children}
    </Box>
);
